/**
 * Live mode's engine (SEO_BRIEF_08 section 2.2): the browser's own speech recognition, the Web
 * Speech API, as `SpeechRecognition` (Chrome, Edge) or `webkitSpeechRecognition` (Safari, and
 * Chrome under its old name). Firefox has neither, and then `recognitionCtor` is null and the panel
 * opens Record instead.
 *
 * `continuous` and `interimResults` are on: words come back while the visitor is still talking,
 * first as a guess (interim, which the panel greys out) and then settled (final, which goes into
 * the box). Recognition ends by itself after a silence in some browsers, so while the visitor has
 * it switched on it is started again; a run of restarts that hear nothing at all, or an error that
 * will not clear by itself, switches it off and says why.
 *
 * THE AUDIO NEVER REACHES OUR SERVER. The browser sends it to its own vendor's speech service
 * (Google, Microsoft, Apple) and hands back text. Nothing here makes a request, and the daily
 * minutes are not touched.
 */

/** The parts of a recognition result list this module reads. */
export interface ResultListLike {
  readonly length: number;
  [index: number]: { readonly isFinal: boolean; readonly 0?: { readonly transcript: string } };
}

export interface RecognitionEventLike {
  readonly resultIndex: number;
  readonly results: ResultListLike;
}

export interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: RecognitionEventLike) => void) | null;
  onerror: ((event: { readonly error: string }) => void) | null;
  onend: (() => void) | null;
}

export type RecognitionCtor = new () => RecognitionLike;

export type LiveFailure = 'blocked' | 'no-mic' | 'network' | 'language' | 'failed';

/** The browser's recognition constructor, or null where it has none (Firefox). */
export function recognitionCtor(scope: object): RecognitionCtor | null {
  const w = scope as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
  const ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return typeof ctor === 'function' ? (ctor as RecognitionCtor) : null;
}

/** An error the recognition names, as what the visitor is told; null for one that clears itself. */
export function liveFailureOf(error: string): LiveFailure | null {
  switch (error) {
    case 'no-speech':
    case 'aborted':
      return null;
    case 'not-allowed':
    case 'service-not-allowed':
      return 'blocked';
    case 'audio-capture':
      return 'no-mic';
    case 'network':
      return 'network';
    case 'language-not-supported':
      return 'language';
    default:
      return 'failed';
  }
}

/**
 * Settled words added to what the box already holds: one space between, none doubled, and the
 * visitor's own edits left as they are.
 */
export function appendFinal(existing: string, words: string): string {
  const add = words.trim();
  if (!add) return existing;
  if (!existing) return add;
  return /\s$/.test(existing) ? existing + add : `${existing} ${add}`;
}

/**
 * The words still being guessed, as they follow the box's text: joined the way `appendFinal` will
 * join them once they settle, so a guess that turns final doesn't move on the page.
 */
export function interimTail(existing: string, guess: string): string {
  const add = guess.trim();
  if (!add) return '';
  return existing && !/\s$/.test(existing) ? ` ${add}` : add;
}

/**
 * The language to start in: the browser's own (`navigator.language`) when it is offered, else the
 * first offered with the same language code, else the first offered.
 */
export function pickLanguage(preferred: string | undefined, offered: readonly string[]): string {
  const fallback = offered[0] ?? 'en-US';
  if (!preferred) return fallback;
  const want = preferred.toLowerCase();
  const exact = offered.find((tag) => tag.toLowerCase() === want);
  if (exact) return exact;
  const code = want.split('-')[0];
  return offered.find((tag) => tag.toLowerCase().split('-')[0] === code) ?? fallback;
}

/**
 * The language Live asks `pickLanguage` for on a page written in `page` (lib/i18n.ts `pageLang`):
 * the browser's own on an English page. A translated page listens for its own language whatever
 * the browser is set to (SEO_BRIEF_10A: /nl/spraak-naar-tekst hears Dutch in an English Chrome),
 * in the browser's own form of it when the browser speaks it (nl-BE).
 */
export function startLanguage(page: string, browser: string | undefined): string | undefined {
  if (page === 'en') return browser;
  return browser?.toLowerCase().split('-')[0] === page ? browser : page;
}

/**
 * A recognition that ends within QUICK_END_MS of starting, MAX_QUICK_RESTARTS times in a row, is
 * a browser refusing without saying so, not a silence: Live switches off rather than loop.
 */
export const QUICK_END_MS = 1000;
export const MAX_QUICK_RESTARTS = 5;

export interface LiveEvents {
  /** The words still being guessed, replacing the last guess ('' when there is none). */
  onInterim?(text: string): void;
  /** Words that are settled. */
  onFinal?(text: string): void;
  /** Switched on or off, by the visitor or by a failure. */
  onSwitch?(on: boolean): void;
  onFail?(failure: LiveFailure): void;
}

export interface Live {
  readonly on: boolean;
  start(lang: string): void;
  stop(): void;
}

export function createLive(
  Ctor: RecognitionCtor,
  events: LiveEvents = {},
  now: () => number = Date.now,
): Live {
  let on = false;
  let current: RecognitionLike | null = null;
  let lang = 'en-US';
  /** When the current recognition started. */
  let startedAt = 0;
  /** Recognitions in a row that ended almost as soon as they started. */
  let quickEnds = 0;

  function switchOff(failure: LiveFailure | null): void {
    const was = on;
    on = false;
    const rec = current;
    current = null;
    if (rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      try {
        rec.abort();
      } catch {
        /* already ended */
      }
    }
    events.onInterim?.('');
    if (was) events.onSwitch?.(false);
    if (failure) events.onFail?.(failure);
  }

  function begin(): void {
    let rec: RecognitionLike;
    try {
      rec = new Ctor();
    } catch {
      switchOff('failed');
      return;
    }
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (event) => {
      if (rec !== current) return;
      quickEnds = 0;
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result?.[0]?.transcript ?? '';
        if (result?.isFinal) events.onFinal?.(text);
        else interim += text;
      }
      events.onInterim?.(interim.trim());
    };
    rec.onerror = (event) => {
      if (rec !== current) return;
      const failure = liveFailureOf(event.error);
      if (failure) switchOff(failure);
    };
    rec.onend = () => {
      if (rec !== current) return;
      events.onInterim?.('');
      if (!on) return;
      // Silence ended it, as some browsers do: start again while it is switched on.
      quickEnds = now() - startedAt < QUICK_END_MS ? quickEnds + 1 : 0;
      if (quickEnds >= MAX_QUICK_RESTARTS) {
        switchOff('failed');
        return;
      }
      begin();
    };
    current = rec;
    startedAt = now();
    try {
      rec.start();
    } catch {
      switchOff('failed');
    }
  }

  return {
    get on() {
      return on;
    },
    start(nextLang: string) {
      if (on) return;
      lang = nextLang || lang;
      on = true;
      quickEnds = 0;
      events.onSwitch?.(true);
      begin();
    },
    stop() {
      if (!on) return;
      const rec = current;
      on = false;
      current = null;
      // stop(), not abort(): the words already spoken still arrive as final.
      if (rec) {
        rec.onerror = null;
        rec.onend = () => events.onInterim?.('');
        const keep = rec.onresult;
        rec.onresult = (event) => {
          // The last results after Stop are still the visitor's words.
          current = rec;
          keep?.(event);
          current = null;
        };
        try {
          rec.stop();
        } catch {
          /* already ended */
        }
      }
      events.onSwitch?.(false);
    },
  };
}
